import type { Direction } from "../direction";
import type { ParticleEmitter } from "../interfaces";
import { Mob } from "../mob";
import { ParticleType } from "../particleType";
import type { PhysicalThingWithHp } from "../physicalThingWithHp";
import { STD_BASE_HP } from "../physicalThingWithHp";
import { DamageType } from "../damageType";
import { ParticleEffect } from "../effects/particleEffect";
import { pushMob, isPushImmune } from "../pushMob";
import { deserializeXY } from "../serialization";
import { ProjectileWithMaxRange } from "../projectileWithMaxRange";
import { Plant } from "../things/plant";
import type { XY } from "../xy";

export class AirBlastShot extends ProjectileWithMaxRange implements ParticleEmitter {
	static key = "AirBlastShot";
	static readonly PUSH_DISTANCE = 5;
	private maxRange: number;

	constructor(xy: XY, direction: Direction, maxRange: number = 7) {
		super(xy, direction);
		this.maxRange = maxRange;
	}

	getParticleType(): ParticleType {
		return ParticleType.AIR_BLAST_TRAIL;
	}

	getMaxRange(): number {
		return this.maxRange;
	}

	protected damageTarget(target: PhysicalThingWithHp): number {
		if (target instanceof Plant) {
			return target.applyDamage(DamageType.EXPLOSION, STD_BASE_HP, this);
		}
		if (target instanceof Mob && !isPushImmune(target)) {
			const r = pushMob(this.board!, target, this.direction, AirBlastShot.PUSH_DISTANCE, this);
			if (r.landedOnFlyable) {
				target.applyDamage(DamageType.EXPLOSION, target.hp, this);
				return 0;
			}
			const bubbleResistant = (target.damageMultipliers.get(DamageType.BUBBLE) ?? 1) === 0;
			if (r.hitWall) {
				return target.applyDamage(DamageType.EXPLOSION, STD_BASE_HP, this);
			}
			if (bubbleResistant) {
				return 0;
			}
			return target.applyDamage(DamageType.EXPLOSION, STD_BASE_HP / 4, this);
		}
		return 0;
	}

	onHit(_damageDealt: number): void {
		this.board!.addEffect(new ParticleEffect(this.xy, ParticleType.AIR_BLAST_BURST, 12));
		this.board!.removeAtEndOfTurn(this);
	}

	onReachingMaxRange(): void {
		this.board!.addEffect(new ParticleEffect(this.xy, ParticleType.AIR_BLAST_BURST, 6));
		this.board!.removeAtEndOfTurn(this);
	}

	serialize(): Record<string, unknown> {
		return { ...super.serialize(), maxRange: this.maxRange };
	}

	static deserialize(data: any): AirBlastShot {
		const shot = new AirBlastShot(deserializeXY(data.xy), data.direction as Direction, data.maxRange ?? 7);
		shot.visualId = data.visualId;
		shot.travelledDistance = data.travelledDistance;
		return shot;
	}
}
